import React from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import { ReactECharts } from "../../components/Charts";
import { useUserInfo } from "../../utils/security";
import { isEmpty } from "lodash";
import { generateColors } from "../../utils/colors";
import { formatCurrency } from "../../utils/numbers";

export const CashflowChart = ({ data, loading, height = 385, disabled, currencyCode }) => {
    const { userInfo } = useUserInfo();
    const theme = useTheme();

    if (isEmpty(data) || loading) {
        return (
            <Box>
                <Skeleton variant="rectangular" sx={{ width: "100%", height }} />
            </Box>
        );
    }

    if (isEmpty(userInfo.transactionTypes)) {
        return <Box sx={{ height }}></Box>;
    }

    const valueFormatter = function (value) {
        return formatCurrency(
            value,
            currencyCode,
            userInfo.currencies,
            2,
            userInfo.decimalDelimiter,
            userInfo.thousandDelimiter
        );
    };

    const chartColors = generateColors(
        [
            theme.palette.primary.light,
            theme.palette.secondary.light,
            theme.palette.tertiaryFixedDim.main,
        ],
        userInfo?.transactionTypes?.length
    );

    const stackedSerie = [];

    for (let i = 0; i < userInfo.transactionTypes.length; i++) {
        const transactionType = userInfo.transactionTypes[i];
        const factor = transactionType.direction === "EXPENDITURE" ? -1 : 1;
        stackedSerie.push({
            name: transactionType.name,
            type: "bar",
            stack: "cashflow",
            yAxisIndex: 0,
            label: {
                show: false,
            },
            data: data.map((nv) => (nv.values?.[transactionType.id] || 0) * factor),
            itemStyle: {
                color: chartColors[i],
            },
            tooltip: {
                valueFormatter,
            },
        });
    }

    let stackedMin = 0;
    let stackedMax = 0;
    let cashflowMin = 0;
    let cashflowMax = 0;

    const cashflowData = [];
    let cashflowCumulated = 0;
    for (let i = 0; i < data.length; i++) {
        let negative = 0;
        let positive = 0;
        for (let j = 0; j < stackedSerie.length; j++) {
            const transactionType = userInfo.transactionTypes[j];

            if (transactionType.direction === "EXPENDITURE") {
                negative += stackedSerie[j].data[i];
            } else {
                positive += stackedSerie[j].data[i];
            }
        }
        stackedMin = Math.min(stackedMin, negative);
        stackedMax = Math.max(stackedMax, positive);

        const cashflowValue = cashflowCumulated + positive + negative;
        cashflowCumulated = cashflowValue;
        cashflowData.push(cashflowValue);

        if (cashflowValue < 0) {
            cashflowMin = Math.min(cashflowMin, cashflowValue);
        } else {
            cashflowMax = Math.max(cashflowMax, cashflowValue);
        }
    }

    console.log(stackedMin, stackedMax, cashflowMin, cashflowMax);

    const scalar = stackedMax === 0 ? cashflowMin / stackedMin : cashflowMax / stackedMax;
    cashflowMin = stackedMin * scalar;
    cashflowMax = stackedMax * scalar;

    return (
        <>
            <Box sx={{ height }}>
                <ReactECharts
                    option={{
                        width: "auto",
                        height: "auto",
                        maintainAspectRatio: false,
                        legend: {
                            show: false,
                        },
                        tooltip: {
                            trigger: "axis",
                            axisPointer: {
                                type: "none",
                            },
                        },
                        xAxis: {
                            type: "category",
                            data: data.map((_, idx) => `${monthNames[idx]} 2024`),
                            axisLine: { show: true },
                            axisTick: {
                                show: false,
                            },
                            axisLabel: {
                                interval: 0,
                                formatter: (v) => v.value,
                                rotate: 0,
                            },
                        },
                        yAxis: [
                            {
                                show: false,
                                min: stackedMin,
                                max: stackedMax,
                            },
                            {
                                show: false,
                                min: cashflowMin,
                                max: cashflowMax,
                            },
                        ],
                        grid: {
                            containLabel: false,
                            left: 0,
                            top: 8,
                            right: 0,
                            bottom: 8,
                            width: "auto",
                            height: "auto",
                        },
                        series: [
                            ...stackedSerie,
                            {
                                name: "Zůstatek",
                                type: "line",
                                yAxisIndex: 1,
                                tooltip: {
                                    valueFormatter,
                                },
                                data: cashflowData,
                                itemStyle: {
                                    color: theme.palette.success.main,
                                },
                            },
                        ],
                        visualMap: [
                            {
                                seriesIndex: stackedSerie.length,
                                inRange: {
                                    color: [theme.palette.error.main, theme.palette.success.main],
                                },
                                show: false,
                            },
                        ],
                    }}
                />
            </Box>
        </>
    );
};

const monthNames = [
    "Leden",
    "Únor",
    "Březen",
    "Duben",
    "Květen",
    "Červen",
    "Červenec",
    "Srpen",
    "Září",
    "Říjen",
    "Listopad",
    "Prosinec",
];
